import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getEnabledDepartmentsConfig } from '../api/configApi';
import {
  DEFAULT_ENABLED_DEPARTMENTS,
  DEPARTMENTS,
  filterSelectableDepartments,
  syncEnabledDepartmentsSession,
} from '../constants/departments';

export function useEnabledDepartments() {
  const query = useQuery({
    queryKey: ['enabled-departments'],
    queryFn: getEnabledDepartmentsConfig,
    staleTime: 5 * 60 * 1000,
  });

  // Memoised on the raw payload: these helpers build fresh arrays on every
  // call, so an unmemoised identity makes any consuming effect re-run forever.
  const enabledDepartments = useMemo(() => {
    const raw = query.data?.enabled?.length ? query.data.enabled : DEFAULT_ENABLED_DEPARTMENTS;
    return syncEnabledDepartmentsSession(raw);
  }, [query.data?.enabled]);

  const selectableDepartments = useMemo(
    () => filterSelectableDepartments(enabledDepartments),
    [enabledDepartments],
  );

  const catalog = useMemo(
    () => (query.data?.catalog?.length ? query.data.catalog : DEPARTMENTS),
    [query.data?.catalog],
  );

  /**
   * Rich catalog rows: { key, displayName, managerId, builtIn }.
   * `key` is the immutable join value stored on users and in every config
   * blob; `displayName` is the only part an admin can edit.
   */
  const departmentRows = useMemo(
    () =>
      query.data?.departments?.length
        ? query.data.departments
        : catalog.map((key) => ({ key, displayName: key, managerId: null, builtIn: true })),
    [query.data?.departments, catalog],
  );

  /** key -> display name, for labelling anywhere departments are shown. */
  const displayNameFor = useMemo(() => {
    const map = new Map(departmentRows.map((d) => [d.key, d.displayName || d.key]));
    return (key) => map.get(key) || key;
  }, [departmentRows]);

  return {
    ...query,
    enabledDepartments,
    selectableDepartments,
    catalog,
    departmentRows,
    displayNameFor,
  };
}
